import styled from 'styled-components'
import { Pill, Tonality } from '@dsny/dsny-component-library'
import {
  LEGACY_BREAKPOINT_QUERIES,
  createResponsiveBreakpointQuery
} from 'styles/breakpoints'

const mobileQuery = createResponsiveBreakpointQuery('mobileDown', {
  legacyQuery: LEGACY_BREAKPOINT_QUERIES.mobileDown
})

export const QUICK_VIEW_VISIBLE_TRACK_ROWS = 7
const QUICK_VIEW_TRACK_ROW_HEIGHT = 48
const QUICK_VIEW_TRACK_ROW_GAP = 4
const QUICK_VIEW_TRACK_ROW_PEEK = 20

export const QuickViewModalCard = styled.div`
  position: relative;
  display: flex;
  flex-direction: column;
  gap: 24px;
  width: 100%;
  color: ${({ theme }) => theme.colors.neutralW90};
`

export const QuickViewCloseButton = styled.button`
  position: absolute;
  top: 0;
  right: 0;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 24px;
  height: 24px;
  padding: 0;
  border: 0;
  background: transparent;
  cursor: pointer;
`

export const QuickViewHeader = styled.div`
  display: flex;
  align-items: flex-start;
  gap: 24px;
  padding-right: 40px;
  min-width: 0;

  @media ${mobileQuery} {
    flex-direction: column;
    gap: 20px;
    padding-right: 32px;
  }
`

export const QuickViewCoverArt = styled.div`
  flex: 0 0 180px;
  width: 180px;
  height: 180px;
  border-radius: 5px;
  overflow: hidden;

  @media ${mobileQuery} {
    width: 140px;
    height: 140px;
    flex-basis: 140px;
  }
`

export const QuickViewContent = styled.div`
  display: flex;
  flex: 1 1 auto;
  min-width: 0;
  flex-direction: column;
  gap: 12px;
`

export const QuickViewPillsRow = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: 8px;
`

export const QuickViewPill = styled.div`
  display: inline-flex;
  align-items: center;
  gap: 4px;
  padding: 4px 8px;
  border-radius: 5px;
  background: ${({ theme }) => theme.colors.neutralW30};
  color: ${({ theme }) => theme.colors.neutralW90};
  font-size: 12px;
  line-height: 1;

  span {
    white-space: nowrap;
  }
`

export const QuickViewTitle = styled.h2`
  margin: 0;
  font-size: 24px;
  line-height: 1.2;
  font-weight: 700;
  color: ${({ theme }) => theme.colors.neutralW90};

  @media ${mobileQuery} {
    font-size: 22px;
  }
`

export const QuickViewMeta = styled.p`
  margin: 0;
  font-size: 14px;
  line-height: 1.5;
  color: ${({ theme }) => theme.colors.neutralW80};
`

export const QuickViewActionsRow = styled.div`
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  gap: 16px;
  margin-top: 4px;
`

export const QuickViewPrimaryButton = styled(Tonality.Button).attrs({
  mode: 'dark',
  setting: 'player',
  variant: 'primary'
})``

export const QuickViewSecondaryButton = styled(Tonality.Button).attrs({
  mode: 'dark',
  setting: 'player',
  variant: 'secondary'
})``

export const QuickViewGhostButton = styled.button`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 36px;
  height: 36px;
  padding: 0;
  border: 0;
  border-radius: 999px;
  background: transparent;
  color: ${({ theme }) => theme.colors.neutralW90};
  cursor: pointer;
  transition: background-color 0.2s ease;

  /* Highlighted while hovered or while its menu is open; never lingers on the
     restored focus after the dropdown closes (data-state returns to "closed").
     Keyboard focus still shows the browser's default outline. */
  &:hover,
  &[data-state='open'] {
    background: ${({ theme }) => theme.colors.neutralW40};
  }

  &:disabled {
    opacity: 0.5;
    cursor: not-allowed;
  }
`

export const QuickViewDivider = styled.div`
  width: 100%;
  height: 1px;
  background: ${({ theme }) => theme.colors.neutralW30};
`

export const QuickViewTrackList = styled.div`
  display: flex;
  flex-direction: column;
  gap: ${QUICK_VIEW_TRACK_ROW_GAP}px;
  width: 100%;
  max-height: calc(
    (${QUICK_VIEW_TRACK_ROW_HEIGHT}px * ${QUICK_VIEW_VISIBLE_TRACK_ROWS}) +
      (${QUICK_VIEW_TRACK_ROW_GAP}px * ${QUICK_VIEW_VISIBLE_TRACK_ROWS - 1}) +
      ${QUICK_VIEW_TRACK_ROW_PEEK}px
  );
  overflow-y: auto;
  padding-right: 8px;

  &::-webkit-scrollbar {
    width: 6px;
  }

  &::-webkit-scrollbar-thumb {
    background: ${({ theme }) => theme.colors.neutralW40};
    border-radius: 999px;
  }

  &::-webkit-scrollbar-track {
    background: transparent;
  }

  @media ${mobileQuery} {
    max-height: calc(100vh - 360px);
  }
`

export const QuickViewTrackRowContainer = styled.div`
  display: grid;
  grid-template-columns: 24px minmax(0, 1fr) 56px auto 32px;
  gap: 16px;
  align-items: center;
  min-height: ${QUICK_VIEW_TRACK_ROW_HEIGHT}px;
  width: 100%;
  padding: 8px;
  border-radius: 5px;
  user-select: none;
  transition: background-color 0.2s ease;

  /* Drive the active look from hover or an open track menu — not focus — so the
     row doesn't stay highlighted after the dropdown restores focus to its
     trigger on close. Radix sets data-state="open" on the trigger inside the
     row, and clears it to "closed" on close. */
  &:hover,
  &:has([data-state='open']) {
    background: ${({ theme }) => theme.colors.neutralW30};
  }

  @media ${mobileQuery} {
    grid-template-columns: 24px minmax(0, 1fr) 32px;
    gap: 12px;
  }
`

export const QuickViewTrackLeadingCell = styled.div`
  position: relative;
  display: flex;
  align-items: center;
  justify-content: center;
  width: 24px;
  min-width: 24px;
  height: 24px;
`

export const QuickViewTrackIndex = styled.div`
  font-size: 12px;
  color: ${({ theme }) => theme.colors.neutralW70};
  text-align: center;
  transition: opacity 0.2s ease;

  ${QuickViewTrackRowContainer}:hover &,
  ${QuickViewTrackRowContainer}:focus-within & {
    opacity: 0;
  }
`

export const QuickViewTrackPlayButton = styled.button`
  position: absolute;
  top: 50%;
  left: 50%;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 24px;
  height: 24px;
  padding: 0;
  border: 0;
  background: transparent;
  color: ${({ theme }) => theme.colors.neutralW100};
  cursor: pointer;
  opacity: 0;
  pointer-events: none;
  transform: translate(-50%, -50%);
  transition: opacity 0.2s ease;

  ${QuickViewTrackRowContainer}:hover &,
  ${QuickViewTrackRowContainer}:has([data-state='open']) & {
    opacity: 1;
    pointer-events: auto;
  }
`

export const QuickViewTrackText = styled.div`
  display: flex;
  min-width: 0;
  flex-direction: column;
  gap: 4px;
`

export const QuickViewTrackTitle = styled.div`
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
  font-size: 14px;
  color: ${({ theme }) => theme.colors.neutralW90};
`

export const QuickViewTrackArtistRow = styled.div`
  display: flex;
  align-items: center;
  gap: 4px;
  min-width: 0;

  svg {
    flex: 0 0 auto;
  }
`

export const QuickViewTrackArtist = styled.div`
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
  font-size: 12px;
  color: ${({ theme }) => theme.colors.neutralW90};
`

export const QuickViewTrackDuration = styled.div`
  font-size: 12px;
  color: ${({ theme }) => theme.colors.neutralW70};
  text-align: right;

  @media ${mobileQuery} {
    display: none;
  }
`

export const QuickViewTrackDownloads = styled.div`
  display: flex;
  flex-wrap: wrap;
  justify-content: flex-end;
  gap: 4px;

  @media ${mobileQuery} {
    display: none;
  }
`

// The library Pill bakes in its own variant hover colours, several of which
// collide with the row's hover background (both land on neutralW30). We drive
// the pill states from the row state instead so they stay legible:
//  - row idle           -> filled (neutralW30) against the modal (neutralW20)
//  - row hover/focus     -> outlined, so it reads against the lighter row
//  - pill hover/focus    -> brighter fill (neutralW40), distinct from the row
// `&&` bumps specificity above the library's single-class variant styles.
export const QuickViewTrackDownloadPill = styled(Pill)`
  && {
    border: 1px solid transparent;
    background-color: ${({ theme }) => theme.colors.neutralW30};
    color: ${({ theme }) => theme.colors.neutralW90};
    transition: background-color 0.2s ease, border-color 0.2s ease;
  }

  ${QuickViewTrackRowContainer}:hover &&,
  ${QuickViewTrackRowContainer}:has([data-state='open']) && {
    background-color: transparent;
    border-color: ${({ theme }) => theme.colors.neutralW70};
  }

  ${QuickViewTrackRowContainer}:hover &&:hover,
  &&:focus-visible {
    background-color: ${({ theme }) => theme.colors.neutralW40};
    border-color: ${({ theme }) => theme.colors.neutralW40};
  }
`

export const QuickViewStatePanel = styled.div`
  display: flex;
  flex-direction: column;
  gap: 12px;
  width: 100%;
`

export const QuickViewSkeletonRow = styled.div`
  display: grid;
  grid-template-columns: 24px minmax(0, 1fr) 56px auto 32px;
  gap: 16px;
  align-items: center;
  min-height: ${QUICK_VIEW_TRACK_ROW_HEIGHT}px;
  width: 100%;
  padding: 8px;

  @media ${mobileQuery} {
    grid-template-columns: 24px minmax(0, 1fr) 32px;
    gap: 12px;
  }
`

export const QuickViewSkeletonBlock = styled.div`
  height: ${({ $height = 12 }) => `${$height}px`};
  width: ${({ $width = '100%' }) => $width};
  border-radius: 999px;
  background: ${({ theme }) => theme.colors.neutralW30};
  opacity: ${({ $dimmed = false }) => ($dimmed ? 0.65 : 1)};
`

export const QuickViewStateCard = styled.div`
  display: flex;
  flex-direction: column;
  align-items: flex-start;
  gap: 12px;
  padding: 8px 0 0;
`

export const QuickViewStateTitle = styled.h3`
  margin: 0;
  font-size: 16px;
  line-height: 1.4;
  color: ${({ theme }) => theme.colors.neutralW90};
`

export const QuickViewStateDescription = styled.p`
  margin: 0;
  font-size: 14px;
  line-height: 1.5;
  color: ${({ theme }) => theme.colors.neutralW70};
`
